import React, { useState, useEffect } from 'react';
import { X, RefreshCw, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

const PANVerificationModal = ({ 
  isOpen, 
  onClose, 
  onDataReceived, 
  initialPAN = ''
}) => {
  const [panNo, setPanNo] = useState(initialPAN);
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [isLoadingCaptcha, setIsLoadingCaptcha] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [verificationResult, setVerificationResult] = useState(null);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved !== null ? JSON.parse(saved) : false;
  });

  // Sync dark mode with localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'darkMode') {
        setDarkMode(e.newValue !== null ? JSON.parse(e.newValue) : false);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setPanNo(initialPAN);
      setCaptchaCode('');
      setError('');
      setVerificationResult(null);
      fetchCaptcha();
    } else {
      // Reset all state when modal closes
      setPanNo('');
      setCaptchaCode('');
      setCaptchaImage('');
      setSessionId('');
      setError('');
      setVerificationResult(null);
    }
  }, [isOpen, initialPAN]);

  // Fetch captcha from GST API (with PAN type to get correct page context)
  const fetchCaptcha = async () => {
    setIsLoadingCaptcha(true);
    setError('');
    
    try {
      // Add ?type=pan to initialize session from searchbypan page (as per successful Postman request)
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/gst/captcha?type=pan`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      const data = await response.json();
      
      if (response.ok && data.sessionId && data.image) {
        setCaptchaImage(data.image);
        setSessionId(data.sessionId);
        console.log('Captcha loaded successfully, session expires in:', data.expiresIn, 'seconds');
      } else {
        setError(data.error || 'Failed to fetch captcha from GST portal');
      }
    } catch (err) {
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError('Network error: Unable to connect to GST verification service.');
      } else {
        setError('Failed to load captcha. Please check your internet connection and try again.');
      }
      console.error('Captcha fetch error:', err);
    } finally {
      setIsLoadingCaptcha(false);
    }
  };

  // Validate PAN format
  const validatePAN = (panValue) => {
    if (!panValue || panValue.length !== 10) {
      return 'PAN must be 10 characters long';
    }
    
    // PAN format: 5 letters + 4 digits + 1 letter (e.g., CLNVP8042F)
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(panValue)) {
      return 'Invalid PAN format (Should be: 5 letters + 4 digits + 1 letter)';
    }
    
    return null;
  };

  // Handle PAN input change
  const handlePANChange = (e) => {
    const value = e.target.value.toUpperCase().trim();
    setPanNo(value);
    setError('');
    setVerificationResult(null);
  };

  // Verify PAN details
  const verifyPAN = async () => {
    // Validate inputs
    const panError = validatePAN(panNo);
    if (panError) {
      setError(panError);
      return;
    }
    
    if (!captchaCode || captchaCode.trim() === '') {
      setError('Please enter the captcha code');
      return;
    }
    
    if (!sessionId) {
      setError('Session expired. Please refresh captcha and try again.');
      return;
    }
    
    setIsVerifying(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/gst/verify-pan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          panNo: panNo,
          captcha: captchaCode,
          sessionId: sessionId
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setVerificationResult(data.data);
        setError('');
        
        // Send data back to parent component after a brief delay
        setTimeout(() => {
          if (onDataReceived) {
            onDataReceived({
              panNo: panNo,
              verificationData: data.data
            });
          }
          onClose();
        }, 2000);
      } else {
        setError(data.error || data.message || 'Failed to verify PAN. Please try again.');
        // Refresh captcha on error
        fetchCaptcha();
        setCaptchaCode('');
      }
    } catch (err) {
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError('Network error: Unable to connect to GST verification service.');
      } else {
        setError('Failed to verify PAN. Please check your internet connection and try again.');
      }
      console.error('PAN verification error:', err);
      // Refresh captcha on error
      fetchCaptcha();
      setCaptchaCode('');
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isVerifying && panNo && captchaCode && sessionId) {
      verifyPAN();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
      <div className={`rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden border-2 ${
        darkMode 
          ? 'bg-slate-900 border-slate-700' 
          : 'bg-white border-purple-200'
      }`}>
        {/* Header */}
        <div className={`px-4 sm:px-6 py-4 flex justify-between items-center ${
          darkMode
            ? 'bg-gradient-to-r from-slate-800 via-blue-900 to-purple-900'
            : 'bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600'
        }`}>
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-xl ${
              darkMode
                ? 'bg-gradient-to-br from-blue-600 to-purple-600'
                : 'bg-gradient-to-br from-white/20 to-white/10'
            }`}>
              <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h2 className={`text-lg sm:text-xl font-black ${
                darkMode
                  ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400'
                  : 'text-white'
              }`}>
                PAN Verification
              </h2>
              <p className={`text-xs sm:text-sm mt-1 ${
                darkMode ? 'text-blue-300' : 'text-blue-100'
              }`}>
                Verify PAN with Government Portal
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isVerifying}
            className={`p-2 rounded-lg transition-all duration-300 transform hover:scale-110 hover:rotate-90 active:scale-95 ${
              darkMode
                ? 'text-red-400 hover:bg-red-900/30 disabled:opacity-50'
                : 'text-white hover:bg-white/20 disabled:opacity-50'
            }`}
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-4 sm:space-y-6">
          {/* PAN Input */}
          <div>
            <label className={`block text-sm font-black uppercase tracking-wider mb-3 ${
              darkMode ? 'text-blue-400' : 'text-purple-700'
            }`}>
              🆔 PAN Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={panNo}
              onChange={handlePANChange}
              onKeyPress={handleKeyPress}
              placeholder="Enter 10-digit PAN (e.g., CLNVP8042F)"
              maxLength={10}
              className={`w-full px-4 py-3 text-sm sm:text-base rounded-xl border-2 font-mono font-bold uppercase transition-all focus:outline-none focus:ring-4 shadow-md ${
                darkMode
                  ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-500 focus:ring-blue-500/30 focus:border-blue-500 hover:border-blue-600'
                  : 'bg-white border-purple-300 text-gray-900 placeholder-gray-400 focus:ring-purple-500/30 focus:border-purple-500 hover:border-purple-400'
              }`}
              disabled={isVerifying}
            />
            <p className={`text-xs mt-2 ${
              darkMode ? 'text-slate-400' : 'text-gray-500'
            }`}>
              Format: 5 letters + 4 digits + 1 letter
            </p>
          </div>

          {/* Captcha Section */}
          <div className="space-y-4">
            <label className={`block text-sm font-black uppercase tracking-wider ${
              darkMode ? 'text-blue-400' : 'text-purple-700'
            }`}>
              🔒 Captcha <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-start">
              {/* Captcha Image */}
              <div className="flex-shrink-0 w-full sm:w-auto">
                {isLoadingCaptcha ? (
                  <div className={`w-full sm:w-48 h-24 rounded-xl border-2 flex items-center justify-center ${
                    darkMode 
                      ? 'bg-slate-800 border-slate-600' 
                      : 'bg-purple-50 border-purple-300'
                  }`}>
                    <Loader2 className={`w-6 h-6 animate-spin ${
                      darkMode ? 'text-blue-400' : 'text-purple-600'
                    }`} />
                  </div>
                ) : captchaImage ? (
                  <div className={`w-full sm:w-48 h-24 rounded-xl border-2 overflow-hidden ${
                    darkMode ? 'border-slate-600' : 'border-purple-300'
                  }`}>
                    <img
                      src={captchaImage}
                      alt="Captcha"
                      className="w-full h-full object-contain bg-white"
                      style={{
                        imageRendering: 'crisp-edges',
                        filter: 'contrast(1.2) brightness(1.1)'
                      }}
                    />
                  </div>
                ) : (
                  <div className={`w-full sm:w-48 h-24 rounded-xl border-2 flex items-center justify-center ${
                    darkMode 
                      ? 'bg-slate-800 border-slate-600' 
                      : 'bg-purple-50 border-purple-300'
                  }`}>
                    <span className={`text-xs ${
                      darkMode ? 'text-slate-400' : 'text-purple-500'
                    }`}>
                      No captcha
                    </span>
                  </div>
                )}
              </div>

              {/* Refresh Button */}
              <button
                onClick={fetchCaptcha}
                disabled={isLoadingCaptcha || isVerifying}
                className={`px-4 py-2.5 rounded-xl font-bold text-sm transition-all shadow-md flex items-center gap-2 min-h-[44px] touch-manipulation ${
                  isLoadingCaptcha || isVerifying
                    ? 'opacity-50 cursor-not-allowed'
                    : darkMode
                      ? 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white'
                      : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white'
                }`}
                title="Refresh Captcha"
              >
                <RefreshCw className={`w-4 h-4 ${isLoadingCaptcha ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>

            {/* Captcha Input */}
            <div>
              <input
                type="text"
                value={captchaCode}
                onChange={(e) => setCaptchaCode(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter captcha code"
                maxLength={6}
                className={`w-full px-4 py-3 text-sm sm:text-base rounded-xl border-2 font-mono font-bold uppercase transition-all focus:outline-none focus:ring-4 shadow-md ${
                  darkMode
                    ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-500 focus:ring-blue-500/30 focus:border-blue-500 hover:border-blue-600'
                    : 'bg-white border-purple-300 text-gray-900 placeholder-gray-400 focus:ring-purple-500/30 focus:border-purple-500 hover:border-purple-400'
                }`}
                disabled={isVerifying || !captchaImage}
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className={`flex items-start gap-2 p-3 rounded-xl border-2 ${
              darkMode
                ? 'bg-red-900/30 border-red-700'
                : 'bg-red-50 border-red-200'
            }`}>
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className={`text-sm font-black uppercase tracking-wider ${
                  darkMode ? 'text-red-400' : 'text-red-800'
                }`}>
                  ❌ Error
                </p>
                <p className={`text-sm ${
                  darkMode ? 'text-red-300' : 'text-red-700'
                }`}>
                  {error}
                </p>
              </div>
            </div>
          )}

          {/* Success Message */}
          {verificationResult && (
            <div className={`flex items-start gap-2 p-4 rounded-xl border-2 ${
              darkMode
                ? 'bg-green-900/30 border-green-600'
                : 'bg-green-50 border-green-200'
            }`}>
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className={`text-sm font-black uppercase tracking-wider mb-2 ${
                  darkMode ? 'text-green-400' : 'text-green-800'
                }`}>
                  ✅ PAN Verified Successfully!
                </p>
                {verificationResult.action === 'Y' && (
                  <div className="space-y-1 text-sm">
                    <p className={darkMode ? 'text-green-300' : 'text-green-700'}>
                      <span className="font-bold">🆔 PAN Number:</span> {panNo}
                    </p>
                    <p className={darkMode ? 'text-green-300' : 'text-green-700'}>
                      <span className="font-bold">📊 Status:</span> Active
                    </p>
                    <p className={`text-xs mt-2 ${
                      darkMode ? 'text-green-400' : 'text-green-600'
                    }`}>
                      Data will be auto-filled in the form.
                    </p>
                  </div>
                )}
                {verificationResult.action === null && (
                  <p className={`text-sm ${
                    darkMode ? 'text-amber-400' : 'text-amber-700'
                  }`}>
                    PAN verified but no additional data available.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-4 sm:px-6 py-4 flex flex-col sm:flex-row justify-end gap-3 border-t-2 ${
          darkMode 
            ? 'bg-slate-800/50 border-slate-700' 
            : 'bg-purple-50/50 border-purple-200'
        }`}>
          <button
            onClick={onClose}
            disabled={isVerifying}
            className={`px-6 py-3 rounded-xl font-bold text-sm transition-all shadow-md min-h-[44px] touch-manipulation ${
              isVerifying
                ? 'opacity-50 cursor-not-allowed'
                : darkMode
                  ? 'text-slate-300 bg-slate-700 border-2 border-slate-600 hover:bg-slate-600 hover:border-slate-500'
                  : 'text-gray-700 bg-white border-2 border-purple-300 hover:bg-purple-50 hover:border-purple-400'
            }`}
          >
            ❌ Cancel
          </button>
          <button
            onClick={verifyPAN}
            disabled={isVerifying || !panNo || !captchaCode || !sessionId}
            className={`px-6 py-3 rounded-xl font-bold text-sm transition-all shadow-md flex items-center justify-center gap-2 min-h-[44px] touch-manipulation ${
              isVerifying || !panNo || !captchaCode || !sessionId
                ? 'opacity-50 cursor-not-allowed bg-gray-400 text-gray-200'
                : darkMode
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white'
                  : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white'
            }`}
          >
            {isVerifying ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Verifying...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                🔍 Verify PAN
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PANVerificationModal;
